import React, { useMemo, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import type { DateRange } from 'react-day-picker';
import 'react-day-picker/style.css';
import './StudentVacationPeriodPicker.css';
import { formatYMD, inclusiveDayCount, parseYMD, stringsToDateRange } from '../utils/dateUtils';

const fmtDisplay = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

export type VacationPeriodChange = { dateFrom: string; dateTo: string };

type Props = {
  dateFrom: string;
  dateTo: string;
  onChange: (next: VacationPeriodChange) => void;
  errors?: { dateFrom?: string; dateTo?: string };
  disabled?: boolean;
};

const StudentVacationPeriodPicker: React.FC<Props> = ({
  dateFrom,
  dateTo,
  onChange,
  errors,
  disabled,
}) => {
  const selected = useMemo(() => stringsToDateRange(dateFrom, dateTo), [dateFrom, dateTo]);

  const [month, setMonth] = useState<Date>(() => {
    if (dateFrom) return parseYMD(dateFrom);
    if (dateTo) return parseYMD(dateTo);
    return new Date();
  });

  // When dateFrom changes (e.g. record loads or is reset), jump the calendar to
  // that month. Adjust state during render instead of in an effect to avoid a
  // cascading render; React applies the new value before the next paint.
  const [prevDateFrom, setPrevDateFrom] = useState(dateFrom);
  if (prevDateFrom !== dateFrom) {
    setPrevDateFrom(dateFrom);
    if (dateFrom) {
      setMonth(parseYMD(dateFrom));
    }
  }

  const handleSelect = (range: DateRange | undefined) => {
    if (!range?.from) {
      onChange({ dateFrom: '', dateTo: '' });
      return;
    }
    if (range.from && range.to) {
      onChange({ dateFrom: formatYMD(range.from), dateTo: formatYMD(range.to) });
      return;
    }
    onChange({ dateFrom: formatYMD(range.from), dateTo: '' });
  };

  const days = inclusiveDayCount(dateFrom, dateTo);
  const summary =
    dateFrom && dateTo && days != null && days >= 1
      ? `${fmtDisplay.format(parseYMD(dateFrom))} → ${fmtDisplay.format(parseYMD(dateTo))}`
      : null;

  return (
    <div className="student-vacation-period-picker">
      <div className="student-vacation-period-picker__header">
        <label className="student-vacation-period-picker__title">Vacation period *</label>
        {days != null && days >= 1 && (
          <span className="student-vacation-period-picker__duration">
            {days} {days === 1 ? 'day' : 'days'}
          </span>
        )}
      </div>

      <p className="student-vacation-period-picker__hint">
        First click: start date. Second click: end date. Or use the button below for a single day.
      </p>

      {summary && <p className="student-vacation-period-picker__summary">{summary}</p>}

      {dateFrom && !dateTo && (
        <button
          type="button"
          className="student-vacation-period-picker__single-day"
          onClick={() => onChange({ dateFrom, dateTo: dateFrom })}
          disabled={disabled}
        >
          Single day only (use start date as end)
        </button>
      )}

      <div className="student-vacation-period-picker__calendar-wrap">
        <DayPicker
          mode="range"
          selected={selected}
          onSelect={handleSelect}
          month={month}
          onMonthChange={setMonth}
          disabled={disabled}
          className="student-vacation-period-picker__rdp"
        />
      </div>

      {(errors?.dateFrom || errors?.dateTo) && (
        <div className="student-vacation-period-picker__errors">
          {errors.dateFrom && <span className="error-text">{errors.dateFrom}</span>}
          {errors.dateTo && <span className="error-text">{errors.dateTo}</span>}
        </div>
      )}
    </div>
  );
};

export default StudentVacationPeriodPicker;
